import type { InvestorProfile, StartupProfile } from '../types/firestore';
import { countryRegions } from '../constants/countries';
import {
  CATALOG_CATEGORY_LABELS,
  getLiCatalogLabel,
  getNearYouLabel,
  getSinceYouAddedFavoritesLabel,
  getWorkingTowardsSdgLabel,
} from '../constants/catalog-categories';
import { INDUSTRY_DISPLAY_MAP } from '../constants/display-mappings';
import { resolveStartupId } from './startupId';

/**
 * Based on Favorites: broaden from favorite countries → continents when
 * industry∩country pool is below this size.
 */
export const COUNTRY_STARTUP_MIN_COUNT = 4;

/** Your Country / Near you — Alice: home country in favorites or prefs and ≥5 startups */
export const YOUR_COUNTRY_FEED_MIN_COUNT = 5;

export type CatalogFeedRow = {
  title: string;
  startups: StartupProfile[];
};

type LocationBucket = {
  key: string;
  title: string;
  startups: StartupProfile[];
};

const LATAM_COUNTRIES = [
  ...countryRegions['CENTRAL AMERICA'],
  ...countryRegions['SOUTH AMERICA'],
  'Mexico',
];

function normalizeCountryName(country: string): string {
  const trimmed = country.trim();
  if (
    trimmed === 'United States' ||
    trimmed === 'USA' ||
    trimmed === 'US' ||
    trimmed === 'United States of America' ||
    trimmed === 'U.S.' ||
    trimmed === 'U.S.A.'
  ) {
    return 'United States';
  }
  return trimmed;
}

/** More-specific regions first; overlaps (Egypt, Nordics, ME/Asia) must not leak into broader buckets. */
const LOCATION_BUCKET_ORDER: Array<{
  key: string;
  title: string;
  match: (country: string) => boolean;
}> = [
  {
    key: 'us',
    title: CATALOG_CATEGORY_LABELS.usBased,
    match: country => normalizeCountryName(country) === 'United States',
  },
  {
    key: 'nordics',
    title: CATALOG_CATEGORY_LABELS.nordicsBased,
    match: country => countryRegions.NORDICS.includes(country),
  },
  {
    key: 'middle-east',
    title: 'Middle East-based',
    match: country => countryRegions['MIDDLE EAST'].includes(country),
  },
  {
    key: 'latam',
    title: CATALOG_CATEGORY_LABELS.latamBased,
    match: country => LATAM_COUNTRIES.includes(country),
  },
  {
    key: 'europe',
    title: CATALOG_CATEGORY_LABELS.europeBased,
    match: country =>
      countryRegions.EUROPE.includes(country) && !countryRegions.NORDICS.includes(country),
  },
  {
    key: 'africa',
    title: CATALOG_CATEGORY_LABELS.africaBased,
    // Egypt is also in MIDDLE EAST — never treat it as Africa-based for feeds
    match: country =>
      countryRegions.AFRICA.includes(country) && !countryRegions['MIDDLE EAST'].includes(country),
  },
  {
    key: 'asia',
    title: 'Asia-based',
    match: country =>
      countryRegions.ASIA.includes(country) && !countryRegions['MIDDLE EAST'].includes(country),
  },
  {
    key: 'oceania',
    title: 'Oceania-based',
    match: country => countryRegions.OCEANIA.includes(country),
  },
  {
    key: 'north-america',
    title: CATALOG_CATEGORY_LABELS.northAmericaBased,
    // Full region (US + Canada + Mexico). When a separate US-based row also
    // exists, US startups are excluded below to avoid duplicate cards.
    match: country =>
      !!country && countryRegions['NORTH AMERICA'].includes(normalizeCountryName(country)),
  },
];

/** Firestore may return a string or sparse data — always work with a clean string[]. */
export function normalizePreferredCountries(
  preferredCountries: string[] | string | undefined | null,
  preferredStates?: string[] | string | undefined | null
): string[] {
  let list: string[] = [];
  if (Array.isArray(preferredCountries)) {
    list = preferredCountries.filter((c): c is string => typeof c === 'string' && !!c.trim());
  } else if (typeof preferredCountries === 'string' && preferredCountries.trim()) {
    list = preferredCountries.includes(',')
      ? preferredCountries
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
      : [preferredCountries.trim()];
  }

  list = list.map(normalizeCountryName).filter(c => c && c !== 'ANYWHERE');

  const states = Array.isArray(preferredStates)
    ? preferredStates
    : typeof preferredStates === 'string' && preferredStates.trim()
      ? [preferredStates.trim()]
      : [];

  if (states.length > 0 && !list.includes('United States')) {
    list = ['United States', ...list];
  }

  return list;
}

export function getIndustryDisplayName(industry: string): string {
  return (
    INDUSTRY_DISPLAY_MAP[industry as keyof typeof INDUSTRY_DISPLAY_MAP] ||
    industry.replace(/-/g, ' ')
  );
}

/** True when startup country is in investor geo prefs (or investor has no geo prefs / ANYWHERE). */
export function startupMatchesPreferredCountries(
  startupCountry: string | undefined,
  preferredCountries: string[] | string | undefined | null,
  preferredStates?: string[] | string | undefined | null
): boolean {
  const prefs = normalizePreferredCountries(preferredCountries, preferredStates);
  if (!prefs.length) return true;
  if (!startupCountry) return false;
  return prefs.includes(normalizeCountryName(startupCountry));
}

export function resolveLocationBucketKey(country: string | undefined): string | null {
  if (!country) return null;
  const normalized = normalizeCountryName(country);
  const bucket = LOCATION_BUCKET_ORDER.find(b => b.match(normalized));
  return bucket?.key ?? null;
}

function filterByCountry(startups: StartupProfile[], country: string): StartupProfile[] {
  const target = normalizeCountryName(country);
  return startups.filter(s => s.country && normalizeCountryName(s.country) === target);
}

/** Industry ∩ continent membership (Narrower Exploration LI rows). */
export function filterByLiMatch(
  startups: StartupProfile[],
  industry: string,
  continentStartups: StartupProfile[]
): StartupProfile[] {
  const inContinent = new Set(
    continentStartups.map(s => resolveStartupId(s)).filter((id): id is string => !!id)
  );
  return startups.filter(s => {
    if (s.industry !== industry) return false;
    const id = resolveStartupId(s);
    return !!id && inContinent.has(id);
  });
}

function filterToContinentUnion(
  startups: StartupProfile[],
  continents: LocationBucket[]
): StartupProfile[] {
  if (continents.length === 0) return startups;
  const inContinents = new Set(
    continents.flatMap(bucket =>
      bucket.startups.map(s => resolveStartupId(s)).filter((id): id is string => !!id)
    )
  );
  return startups.filter(s => {
    const id = resolveStartupId(s);
    return !!id && inContinents.has(id);
  });
}

/** Industries ranked by frequency among favorites (newest-first tie-break). */
export function industriesFromFavorites(favoriteStartups: StartupProfile[]): string[] {
  const counts = new Map<string, number>();
  const firstIndex = new Map<string, number>();
  favoriteStartups.forEach((favorite, index) => {
    const industry = typeof favorite.industry === 'string' ? favorite.industry.trim() : '';
    if (!industry) return;
    counts.set(industry, (counts.get(industry) || 0) + 1);
    if (!firstIndex.has(industry)) firstIndex.set(industry, index);
  });
  return Array.from(counts.keys()).sort((a, b) => {
    const byCount = (counts.get(b) || 0) - (counts.get(a) || 0);
    if (byCount !== 0) return byCount;
    return (firstIndex.get(a) || 0) - (firstIndex.get(b) || 0);
  });
}

/**
 * Continents for Narrower Exploration.
 * Favorites continents first (when any), then remaining country prefs.
 */
export function resolveExplorationContinents(
  investorProfile: InvestorProfile | null | undefined,
  favoriteStartups: StartupProfile[],
  allStartups: StartupProfile[]
): LocationBucket[] {
  const prefBuckets = getPreferredLocationBuckets(
    investorProfile?.countries,
    allStartups,
    investorProfile?.states
  );

  if (favoriteStartups.length === 0) return prefBuckets;

  const favoriteCountries = favoriteStartups
    .map(s => (typeof s.country === 'string' ? s.country : ''))
    .filter(Boolean);
  const favBuckets = getPreferredLocationBuckets(favoriteCountries, allStartups);

  const seen = new Set<string>();
  const merged: LocationBucket[] = [];
  for (const bucket of [...favBuckets, ...prefBuckets]) {
    if (seen.has(bucket.key)) continue;
    seen.add(bucket.key);
    merged.push(bucket);
  }
  return merged;
}

/**
 * Industries for Narrower Exploration / LI Match.
 * Favorites industries first (when any), then unused prefs.
 */
export function resolveExplorationIndustries(
  investorProfile: InvestorProfile | null | undefined,
  favoriteStartups: StartupProfile[]
): string[] {
  const preferred = normalizePreferredIndustries(investorProfile?.industries);
  if (favoriteStartups.length === 0) return preferred;

  const fromFavorites = industriesFromFavorites(favoriteStartups);
  if (fromFavorites.length === 0) return preferred;

  const seen = new Set(fromFavorites);
  return [...fromFavorites, ...preferred.filter(industry => !seen.has(industry))];
}

export type LiIndustryRow = {
  industry: string;
  continentTitles: string[];
  startups: StartupProfile[];
};

/**
 * LI Match rows: industry ∩ all selected continents.
 * Industry #1 / #2 = industries with the most / 2nd-most startups in those continents
 * (among candidate industries from prefs or favorites).
 */
export function buildLiIndustryRows(
  industries: string[],
  continents: LocationBucket[],
  allStartups: StartupProfile[],
  maxRows = 2
): LiIndustryRow[] {
  if (!industries.length || !continents.length) return [];

  const continentTitles = continents.map(c => c.title);

  const scored = industries
    .map((industry, industryIndex) => {
      const inIndustry = allStartups.filter(s => s.industry === industry);
      const startups = filterToContinentUnion(inIndustry, continents);
      return { industry, startups, count: startups.length, industryIndex };
    })
    .filter(row => row.count > 0);

  // Most startups in selected continents first; ties keep source order
  scored.sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return a.industryIndex - b.industryIndex;
  });

  return scored.slice(0, maxRows).map(({ industry, startups }) => ({
    industry,
    continentTitles,
    startups,
  }));
}

/** Alice: max startups in Based on your Favorites before ranking layers exist. */
export const BASED_ON_FAVORITES_MAX = 23;

/**
 * Based on your Favorites membership (Alice LI Match):
 * - Industry: exact match any industry among all favorites
 * - Location: favorite countries when that intersection has ≥4 startups;
 *   otherwise broaden to all continents derived from favorite countries
 * - Excludes already-favorited startups
 */
export function buildBasedOnFavoritesStartups(
  favoriteStartups: StartupProfile[],
  allStartups: StartupProfile[],
  options?: { excludeIds?: Set<string>; max?: number }
): StartupProfile[] {
  if (favoriteStartups.length === 0) return [];

  const favoriteIds = new Set(
    favoriteStartups.map(s => resolveStartupId(s)).filter((id): id is string => !!id)
  );
  const excludeIds = options?.excludeIds ?? new Set<string>();
  const max = options?.max ?? BASED_ON_FAVORITES_MAX;

  const industries = new Set(
    favoriteStartups
      .map(s => (typeof s.industry === 'string' ? s.industry.trim() : ''))
      .filter(Boolean)
  );
  if (industries.size === 0) return [];

  const favoriteCountries = favoriteStartups
    .map(s => (typeof s.country === 'string' ? s.country.trim() : ''))
    .filter(Boolean);
  if (favoriteCountries.length === 0) return [];

  const normalizedFavCountries = new Set(
    favoriteCountries.map(c => normalizeCountryName(c)).filter(Boolean)
  );

  const industryPool = allStartups.filter(startup => {
    const id = resolveStartupId(startup);
    if (!id || favoriteIds.has(id) || excludeIds.has(id)) return false;
    return typeof startup.industry === 'string' && industries.has(startup.industry);
  });

  const byFavoriteCountry = industryPool.filter(
    s => s.country && normalizedFavCountries.has(normalizeCountryName(s.country))
  );

  // ≥4 at country level → keep countries; else broaden to favorite continents
  if (byFavoriteCountry.length >= COUNTRY_STARTUP_MIN_COUNT) {
    return byFavoriteCountry.slice(0, max);
  }

  const continentBuckets = getPreferredLocationBuckets(favoriteCountries, allStartups);
  if (continentBuckets.length === 0) {
    return byFavoriteCountry.slice(0, max);
  }

  const inContinents = filterToContinentUnion(industryPool, continentBuckets);
  return inContinents.slice(0, max);
}

/** SDG #1: first selected SDG, else platform most-common. */
export function resolveSdgOne(
  investorProfile: InvestorProfile | null | undefined,
  allStartups: StartupProfile[]
): number | null {
  const selected = normalizeStartupSdgs(investorProfile?.selectedSDGs);
  if (selected.length > 0) return selected[0] ?? null;
  return getMostCommonSdg(allStartups);
}

/** Match catalog API: accept number[], string[], or legacy comma/JSON string. */
function normalizeStartupSdgs(raw: unknown): number[] {
  let values: unknown[] = [];
  if (Array.isArray(raw)) {
    values = raw;
  } else if (typeof raw === 'string' && raw.trim()) {
    const trimmed = raw.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) values = parsed;
      } catch {
        values = [];
      }
    } else {
      values = trimmed.split(',');
    }
  }

  const out: number[] = [];
  for (const value of values) {
    const n =
      typeof value === 'number' ? value : typeof value === 'string' ? Number(value.trim()) : NaN;
    if (Number.isFinite(n) && n >= 1 && n <= 17) out.push(n);
  }
  return out;
}

function filterBySdg(startups: StartupProfile[], sdg: number): StartupProfile[] {
  return startups.filter(s => normalizeStartupSdgs(s.selectedSDGs).includes(sdg));
}

function filterByFounderTag(startups: StartupProfile[], tag: string): StartupProfile[] {
  const normalizedTag = tag.toLowerCase();
  return startups.filter(s => {
    if (!Array.isArray(s.founderRepresentation)) return false;
    return s.founderRepresentation.some(
      rep => typeof rep === 'string' && rep.toLowerCase() === normalizedTag
    );
  });
}

function getMostCommonSdg(startups: StartupProfile[]): number | null {
  const counts = new Map<number, number>();
  startups.forEach(startup => {
    normalizeStartupSdgs(startup.selectedSDGs).forEach(sdg => {
      counts.set(sdg, (counts.get(sdg) || 0) + 1);
    });
  });
  let best: number | null = null;
  let bestCount = 0;
  counts.forEach((count, sdg) => {
    if (count > bestCount) {
      best = sdg;
      bestCount = count;
    }
  });
  return best;
}

const FOUNDER_TAG_LABELS: Record<string, string> = {
  woman: CATALOG_CATEGORY_LABELS.womenLed,
  poc: 'POC-led',
  lgbtq: 'LGBTQ+-led',
  immigrant: 'Immigrant-led',
  personWithDisabilities: 'Founders with disabilities',
  seniorEntrepreneur: 'Senior entrepreneur-led',
  teenEntrepreneur: 'Teen entrepreneur-led',
};

function normalizePreferredIndustries(industries: string[] | string | undefined | null): string[] {
  if (Array.isArray(industries)) {
    return industries.filter((i): i is string => typeof i === 'string' && !!i.trim());
  }
  if (typeof industries === 'string' && industries.trim()) {
    return industries.includes(',')
      ? industries
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
      : [industries.trim()];
  }
  return [];
}

/** Exclusive sets used for feed membership (no dual-listed leaks). */
const REGION_COUNTRY_SETS: Record<string, string[]> = {
  nordics: countryRegions.NORDICS,
  europe: countryRegions.EUROPE.filter(c => !countryRegions.NORDICS.includes(c)),
  us: ['United States'],
  latam: LATAM_COUNTRIES,
  africa: countryRegions.AFRICA.filter(c => !countryRegions['MIDDLE EAST'].includes(c)),
  asia: countryRegions.ASIA.filter(c => !countryRegions['MIDDLE EAST'].includes(c)),
  'middle-east': countryRegions['MIDDLE EAST'],
  oceania: countryRegions.OCEANIA,
  'north-america': countryRegions['NORTH AMERICA'],
};

/** Full region lists as saved by CountryRegionSelector (includes dual memberships like Egypt). */
const FULL_REGION_PREF_LISTS: Record<string, string[]> = {
  nordics: countryRegions.NORDICS,
  europe: countryRegions.EUROPE,
  us: ['United States'],
  latam: LATAM_COUNTRIES,
  africa: countryRegions.AFRICA,
  asia: countryRegions.ASIA,
  'middle-east': countryRegions['MIDDLE EAST'],
  oceania: countryRegions.OCEANIA,
  'north-america': countryRegions['NORTH AMERICA'],
};

/** True when prefs look like a region "select all" (many countries from one continent). */
function isRegionSelectAll(bucketKey: string, preferredCountries: string[]): boolean {
  const regionCountries = FULL_REGION_PREF_LISTS[bucketKey] || REGION_COUNTRY_SETS[bucketKey] || [];
  // Allow small regions (e.g. North America has only 3 countries). Still require a
  // high overlap ratio so a couple of explicit picks don't look like "select all".
  if (regionCountries.length < 2) return false;
  const overlap = regionCountries.filter(c => preferredCountries.includes(c)).length;
  return overlap >= Math.ceil(regionCountries.length * 0.8);
}

function selectAllRegionCoveringCountry(
  country: string,
  selectAllRegionKeys: Set<string>
): string | null {
  for (const key of Array.from(selectAllRegionKeys)) {
    const list = FULL_REGION_PREF_LISTS[key] || [];
    if (list.includes(country)) return key;
  }
  return null;
}

/**
 * Ordered location feeds from investor preferences.
 *
 * Always uses region/catalog buckets (US-based, Africa-based, …) with *all*
 * startups in that bucket — never a single country under a continent title
 * (that made "Africa-based" show only 3 Nigerian startups when Nigeria had ≥3).
 *
 * - Region select-all and explicit country picks map to the same bucket titles
 * - Explicit country picks rank above region select-all
 * - Otherwise follows preference list order
 */
export function getPreferredLocationBuckets(
  preferredCountries: string[] | string | undefined,
  allStartups: StartupProfile[],
  preferredStates?: string[] | string | undefined
): LocationBucket[] {
  const countries = normalizePreferredCountries(preferredCountries, preferredStates);
  if (!countries.length) return [];

  // Detect region select-alls so Egypt/Nigeria/etc. from "Africa" collapse to one feed.
  const selectAllRegionKeys = new Set(
    Object.keys(REGION_COUNTRY_SETS).filter(key => isRegionSelectAll(key, countries))
  );

  type PendingContinent = {
    key: string;
    title: string;
    earliestIndex: number;
    selectAll: boolean;
    match: (country: string) => boolean;
  };

  const continentByKey = new Map<string, PendingContinent>();

  const ensureContinentByKey = (bucketKey: string, index: number) => {
    const bucketDef = LOCATION_BUCKET_ORDER.find(b => b.key === bucketKey);
    if (!bucketDef) return;
    const existing = continentByKey.get(bucketDef.key);
    if (existing) {
      existing.earliestIndex = Math.min(existing.earliestIndex, index);
      return;
    }
    continentByKey.set(bucketDef.key, {
      key: bucketDef.key,
      title: bucketDef.title,
      earliestIndex: index,
      selectAll: selectAllRegionKeys.has(bucketDef.key),
      match: bucketDef.match,
    });
  };

  const ensureContinent = (country: string, index: number) => {
    const coveringSelectAll = selectAllRegionCoveringCountry(country, selectAllRegionKeys);
    if (coveringSelectAll) {
      ensureContinentByKey(coveringSelectAll, index);
      return;
    }
    const bucketKey = resolveLocationBucketKey(country);
    if (bucketKey) ensureContinentByKey(bucketKey, index);
  };

  countries.forEach((country, index) => {
    ensureContinent(country, index);
  });

  const hasUsBucket = continentByKey.has('us');

  const continentFeeds: Array<LocationBucket & { earliestIndex: number; selectAll: boolean }> =
    Array.from(continentByKey.values())
      .map(c => ({
        key: c.key,
        title: c.title,
        startups: allStartups.filter(s => {
          if (!c.match(s.country)) return false;
          // Avoid duplicating US cards under both US-based and North America-based.
          if (
            c.key === 'north-america' &&
            hasUsBucket &&
            normalizeCountryName(s.country || '') === 'United States'
          ) {
            return false;
          }
          return true;
        }),
        earliestIndex: c.earliestIndex,
        selectAll: c.selectAll,
      }))
      .filter(bucket => bucket.startups.length > 0);

  // Rank: non-select-all continents before select-all; then preference order
  const ranked = [...continentFeeds].sort((a, b) => {
    if (a.selectAll !== b.selectAll) return a.selectAll ? 1 : -1;
    return a.earliestIndex - b.earliestIndex;
  });

  return ranked.map(({ key, title, startups }) => ({ key, title, startups }));
}

export type InvestorCatalogFeedInput = {
  investorProfile: InvestorProfile | null;
  allStartups: StartupProfile[];
  matchedStartups: StartupProfile[];
  favoriteStartups: StartupProfile[];
  womenLedStartups: StartupProfile[];
  pocOwnedStartups: StartupProfile[];
  lgbtqOwnedStartups: StartupProfile[];
  mostPopularStartups: StartupProfile[];
  /** @deprecated Row title no longer uses the company name; kept for callers. */
  selectedFavorite: StartupProfile | null;
  /** Similar startups for "Based on your Favorites" (only shown when favorites exist). */
  similarToFavoriteStartups: StartupProfile[];
  /**
   * Startup ids the investor has already opened.
   * Prefer newest-first order (array) so Previously viewed is reverse chronological.
   * A Set also works (order then follows SFY matches, then catalog).
   */
  viewedStartupIds?: Set<string> | string[] | null;
};

/** @deprecated Use InvestorCatalogFeedInput */
export type PostFavoritesFeedInput = InvestorCatalogFeedInput;

function normalizeViewedStartupIds(viewedStartupIds?: Set<string> | string[] | null): {
  viewedIds: Set<string>;
  viewedIdsOrdered: string[];
} {
  if (!viewedStartupIds) {
    return { viewedIds: new Set(), viewedIdsOrdered: [] };
  }
  if (viewedStartupIds instanceof Set) {
    const viewedIdsOrdered = Array.from(viewedStartupIds).filter(
      (id): id is string => typeof id === 'string' && !!id
    );
    return { viewedIds: new Set(viewedIdsOrdered), viewedIdsOrdered };
  }
  const viewedIdsOrdered: string[] = [];
  const viewedIds = new Set<string>();
  for (const id of viewedStartupIds) {
    if (typeof id !== 'string' || !id || viewedIds.has(id)) continue;
    viewedIds.add(id);
    viewedIdsOrdered.push(id);
  }
  return { viewedIds, viewedIdsOrdered };
}

/**
 * Fixed catalog feed order from profile creation onward.
 * Featured Startup remains the dashboard hero above these rows.
 *
 * Most relevant (top):
 * 1. Specially for you (LISA matches minus favorites — viewed ones keep the corner badge)
 * 2. Your Favorites
 * 3. Based on your Favorites (when favorites + similar startups exist; badge says Since you follow …)
 * 4. Previously viewed (newest first when ordered)
 *
 * Narrower Exploration / LI Match (Alice):
 * 5–6. Industry rows ∩ all selected continents; industries ranked by most startups
 *     in those continents. Titles: "{industry} based in {continent(s)}"
 * 7. Other industries in your preferred locations (exact preferred countries ∩ not preferred industries; max 23)
 * 8. Your industries in other locations (preferred industries ∩ outside preferred countries; max 23)
 * 9. Women-led (always) + all continents from favorites & prefs
 * 10. SDG #1 + all continents (title = SDG: {SDG name})
 * 11. Founder tag #1 + all continents (skips women so it isn’t duplicated)
 * 12. Your Country / Near you (home country in favorites or prefs + ≥5 startups)
 */
export function buildInvestorCatalogFeed(input: InvestorCatalogFeedInput): CatalogFeedRow[] {
  const {
    investorProfile,
    allStartups,
    matchedStartups,
    favoriteStartups,
    womenLedStartups,
    pocOwnedStartups,
    lgbtqOwnedStartups,
    similarToFavoriteStartups,
    viewedStartupIds,
  } = input;
  void input.mostPopularStartups; // kept on input for callers; not in Narrower Exploration A–B
  const { selectedFavorite } = input;

  const rows: CatalogFeedRow[] = [];
  const pushRow = (title: string, startups: StartupProfile[]) => {
    if (!title) return;
    rows.push({ title, startups });
  };

  const { viewedIds, viewedIdsOrdered } = normalizeViewedStartupIds(viewedStartupIds);

  const startupsById = new Map<string, StartupProfile>();
  for (const startup of [...matchedStartups, ...allStartups]) {
    const id = resolveStartupId(startup);
    if (!id || startupsById.has(id)) continue;
    startupsById.set(id, startup);
  }

  // Reverse chronological when ordered ids are provided; otherwise SFY order then catalog.
  const previouslyViewed: StartupProfile[] = [];
  const seenPreviouslyViewed = new Set<string>();
  const favoriteIds = new Set(
    favoriteStartups.map(s => resolveStartupId(s)).filter((id): id is string => !!id)
  );
  for (const id of viewedIdsOrdered) {
    const startup = startupsById.get(id);
    if (!startup || seenPreviouslyViewed.has(id) || favoriteIds.has(id)) continue;
    seenPreviouslyViewed.add(id);
    previouslyViewed.push(startup);
  }
  // Fallback for Set-only callers with no stable order: append remaining viewed in catalog order
  for (const startup of [...matchedStartups, ...allStartups]) {
    const id = resolveStartupId(startup);
    if (!id || !viewedIds.has(id) || seenPreviouslyViewed.has(id) || favoriteIds.has(id)) continue;
    seenPreviouslyViewed.add(id);
    previouslyViewed.push(startup);
  }

  // 1. Specially for you — LISA matches only; favorites leave SFY
  const speciallyForYou = matchedStartups.filter(startup => {
    const id = resolveStartupId(startup);
    return !!id && !favoriteIds.has(id);
  });
  pushRow(CATALOG_CATEGORY_LABELS.speciallyForYou, speciallyForYou);

  // 2. Your Favorites (always — empty state rendered by StartupRow)
  pushRow(CATALOG_CATEGORY_LABELS.yourFavorites, favoriteStartups);

  // 3. Since you follow … (directly under Favorites when similar startups exist)
  if (favoriteStartups.length > 0 && similarToFavoriteStartups.length > 0) {
    const favoriteName =
      selectedFavorite?.companyName?.trim() || favoriteStartups[0]?.companyName?.trim();
    pushRow(getSinceYouAddedFavoritesLabel(favoriteName), similarToFavoriteStartups);
  }

  // 4. Previously viewed (hidden when empty)
  if (previouslyViewed.length > 0) {
    pushRow(CATALOG_CATEGORY_LABELS.previouslyViewed, previouslyViewed);
  }

  // --- Narrower Exploration / LI Match ---
  const explorationIndustries = resolveExplorationIndustries(investorProfile, favoriteStartups);
  const explorationContinents = resolveExplorationContinents(
    investorProfile,
    favoriteStartups,
    allStartups
  );

  // 5–6. Industry ∩ all selected continents; #1/#2 = most startups in those continents
  // Title: "{industry} based in {continent}, and {continent}" (Alice multi-continent)
  const liRows = buildLiIndustryRows(explorationIndustries, explorationContinents, allStartups, 2);
  for (const row of liRows) {
    pushRow(
      getLiCatalogLabel(getIndustryDisplayName(row.industry), row.continentTitles),
      row.startups
    );
  }

  // 7–8. Cross industry/location options — exact preferred countries only
  // (not favorite continents, and not leftover US states when US isn’t selected)
  const preferredIndustries = new Set(normalizePreferredIndustries(investorProfile?.industries));
  let preferredCountriesForOther = normalizePreferredCountries(investorProfile?.countries, null);
  if (
    preferredCountriesForOther.length === 0 &&
    normalizePreferredCountries([], investorProfile?.states).includes('United States')
  ) {
    preferredCountriesForOther = ['United States'];
  }
  if (preferredCountriesForOther.length > 0) {
    const preferredCountrySet = new Set(preferredCountriesForOther);

    // 7. Other industries in your preferred locations
    const otherIndustryStartups = allStartups
      .filter(startup => {
        const country = startup.country ? normalizeCountryName(startup.country) : '';
        const industry = typeof startup.industry === 'string' ? startup.industry.trim() : '';
        return (
          !!country &&
          preferredCountrySet.has(country) &&
          !!industry &&
          !preferredIndustries.has(industry)
        );
      })
      .slice(0, BASED_ON_FAVORITES_MAX);
    if (otherIndustryStartups.length > 0) {
      pushRow(CATALOG_CATEGORY_LABELS.otherIndustriesInYourLocation, otherIndustryStartups);
    }

    // 8. Your industries in other locations
    if (preferredIndustries.size > 0) {
      const yourIndustriesElsewhere = allStartups
        .filter(startup => {
          const country = startup.country ? normalizeCountryName(startup.country) : '';
          const industry = typeof startup.industry === 'string' ? startup.industry.trim() : '';
          return (
            !!country &&
            !preferredCountrySet.has(country) &&
            !!industry &&
            preferredIndustries.has(industry)
          );
        })
        .slice(0, BASED_ON_FAVORITES_MAX);
      if (yourIndustriesElsewhere.length > 0) {
        pushRow(CATALOG_CATEGORY_LABELS.yourIndustriesInOtherLocations, yourIndustriesElsewhere);
      }
    }
  }

  // 9. Women-led (always) + all continents from favorites & prefs
  const womenLedFromCatalog = filterByFounderTag(allStartups, 'woman');
  const womenLedBase =
    womenLedFromCatalog.length >= womenLedStartups.length ? womenLedFromCatalog : womenLedStartups;
  pushRow(
    CATALOG_CATEGORY_LABELS.womenLed,
    filterToContinentUnion(womenLedBase, explorationContinents)
  );

  // 10. SDG #1 + all continents — title: SDG: {SDG name}
  const sdgOne = resolveSdgOne(investorProfile, allStartups);
  if (sdgOne != null) {
    const sdgTitle = getWorkingTowardsSdgLabel(sdgOne);
    const sdgStartups = filterToContinentUnion(
      filterBySdg(allStartups, sdgOne),
      explorationContinents
    );
    if (sdgStartups.length > 0) {
      pushRow(sdgTitle, sdgStartups);
    }
  }

  // 11. Founder tag #1 + all continents (skip women — already in Women-led)
  const selectedFounderTags: string[] = Array.isArray(investorProfile?.founderRepresentation)
    ? (investorProfile?.founderRepresentation ?? [])
    : [];
  const founderTagOne = selectedFounderTags.find(tag => tag && tag !== 'woman') || null;
  if (founderTagOne) {
    let founderStartups: StartupProfile[] = [];
    if (founderTagOne === 'poc') founderStartups = pocOwnedStartups;
    else if (founderTagOne === 'lgbtq') founderStartups = lgbtqOwnedStartups;
    else founderStartups = filterByFounderTag(allStartups, founderTagOne);

    const title = FOUNDER_TAG_LABELS[founderTagOne] || `${founderTagOne}-led`;
    const scoped = filterToContinentUnion(founderStartups, explorationContinents);
    if (scoped.length > 0) {
      pushRow(title, scoped);
    }
  }

  // 12. Your Country — home country in favorites or prefs and ≥5 startups
  const preferredCountries = normalizePreferredCountries(
    investorProfile?.countries,
    investorProfile?.states
  );
  const investorCountry = investorProfile?.country
    ? normalizeCountryName(investorProfile.country)
    : '';
  const homeInPrefs = !!investorCountry && preferredCountries.includes(investorCountry);
  const homeInFavorites =
    !!investorCountry &&
    favoriteStartups.some(
      fav => fav.country && normalizeCountryName(fav.country) === investorCountry
    );
  if (investorCountry && (homeInPrefs || homeInFavorites)) {
    const nearYouStartups = filterByCountry(allStartups, investorCountry);
    if (nearYouStartups.length >= YOUR_COUNTRY_FEED_MIN_COUNT) {
      pushRow(getNearYouLabel(investorCountry), nearYouStartups);
    }
  }

  return rows;
}

/** @deprecated Use buildInvestorCatalogFeed */
export const buildPostFavoritesCatalogFeed = buildInvestorCatalogFeed;
